import { useCallback, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import Modal from "~components/Modal";
import Select from "~components/Form/components/Select";
import InputField from "~components/Form/components/InputField";
import { Form, Formik } from "formik";
import { Textarea } from "@chakra-ui/react";
import usePrazoPagamento from "~/fetchs/usePrazoPagamento";
import useMotivo from "~/fetchs/useMotivo";
import useModalRenovarMassivo from "./useModalRenovarMassivo";
import "~pages/PricesTableV2/components/Modal/style.scss";

const RenovarMassivoModal = ({ isOpen, onClose, onSubmit, selectedRows = [] }) => (
  <Modal
    isOpen={isOpen}
    onClose={onClose}
    sub={<Content onClose={onClose} onSubmit={onSubmit} selectedRows={selectedRows} />}
    hideButtonTwo={true}
    hideButtonOne={true}
  />
);

RenovarMassivoModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
  selectedRows: PropTypes.array,
};

const Content = ({ onClose, onSubmit, selectedRows }) => {
  const formikRef = useRef();
  const { initial, validation } = useModalRenovarMassivo();

  const { onChangePrazoPagamento, prazoPagamentoSelect } = usePrazoPagamento(formikRef, "prazoVigenciaId");
  const { optionMotivos, onChangeMotivo, motivoSelect } = useMotivo({ formikRef });

  const optionPrazos = useMemo(
    () => [
      { label: "1 Mês", value: { months: 1 } },
      { label: "3 Meses", value: { months: 3 } },
      { label: "6 Meses", value: { months: 6 } },
      { label: "1 Ano", value: { years: 1 } },
      { label: "Personalizado", value: "personalizado" },
    ],
    []
  );

  const calculateDates = useCallback((value) => {
    const startDate = new Date();
    const finalDate = new Date();

    if (value !== "personalizado") {
      finalDate.setDate(finalDate.getDate() + (value.days || 0));
      finalDate.setMonth(finalDate.getMonth() + (value.months || 0));
      finalDate.setFullYear(finalDate.getFullYear() + (value.years || 0));
    }

    const toIso = (d) =>
      [
        d.getFullYear(),
        (d.getMonth() + 1).toString().padStart(2, "0"),
        d.getDate().toString().padStart(2, "0"),
      ].join("-");

    return {
      startDateString: toIso(startDate),
      finalDateString: value === "personalizado" ? "" : toIso(finalDate),
    };
  }, []);

  const handleChangePrazo = useCallback(
    (e) => {
      onChangePrazoPagamento(e);
      const { startDateString, finalDateString } = calculateDates(e.value);
      if (formikRef.current) {
        formikRef.current.setValues((prev) => ({
          ...prev,
          prazoVigenciaId: e.value,
          inicioVigencia: startDateString,
          fimVigencia: finalDateString,
        }));
      }
    },
    [onChangePrazoPagamento, calculateDates]
  );

  const handleChangeDates = useCallback(() => {
    onChangePrazoPagamento({ label: "Personalizado", value: "personalizado" });
    if (formikRef.current) {
      formikRef.current.setFieldValue("prazoVigenciaId", "personalizado");
    }
  }, [onChangePrazoPagamento]);

  const handleJustificativaChange = useCallback((e) => {
    if (formikRef.current) {
      formikRef.current.setFieldValue("justificativa", e.target.value);
    }
  }, []);

  const handleSubmit = useCallback(
    (values) => {
      console.log("Renovar massivo - linhas selecionadas:", selectedRows);
      console.log("Valores do formulário:", values);
      onSubmit?.(values, selectedRows);
      onClose?.();
    },
    [selectedRows, onSubmit, onClose]
  );

  const maxLength = 200;

  return (
    <main className="prices-table-modal-informacoes-adicionais">
      <h1 className="title">Informações Adicionais:</h1>
      <Formik
        initialValues={initial}
        onSubmit={handleSubmit}
        validationSchema={validation}
        innerRef={formikRef}
        enableReinitialize={true}
      >
        {({ values, errors, touched }) => (
          <Form className="form">
            <Select
              label={<>Informe o prazo de vigência das negociações solicitadas:</>}
              name="prazoVigenciaId"
              options={optionPrazos}
              onChange={handleChangePrazo}
              errors={errors.prazoVigenciaId}
              touched={touched.prazoVigenciaId}
              isRequired={true}
              {...prazoPagamentoSelect}
            />
            <div className="flex gap-x-4">
              <InputField
                labelName="Início:"
                inputName="inicioVigencia"
                inputMask="offmask"
                inputType="date"
                isRequired={true}
                isDisabled={false}
                onChangeEvent={handleChangeDates}
                errors={errors.inicioVigencia}
                touched={touched.inicioVigencia}
              />
              <InputField
                labelName="Fim:"
                inputName="fimVigencia"
                inputMask="offmask"
                inputType="date"
                isRequired={true}
                isDisabled={false}
                onChangeEvent={handleChangeDates}
                errors={errors.fimVigencia}
                touched={touched.fimVigencia}
              />
            </div>
            <Select
              name="motivoId"
              label={<>Informe o Motivo das negociações solicitadas:</>}
              options={optionMotivos}
              errors={errors.motivoId}
              touched={touched.motivoId}
              onChange={(e) => onChangeMotivo(e)}
              isRequired={true}
              {...motivoSelect}
            />
            <label className="label">
              <p className="desc">
                Informe a Justificativa das negociações solicitadas:{" "}
                <span style={{ color: "#e53e3e" }}>*</span>
              </p>
              <Textarea
                name="justificativa"
                placeholder="Digite sua descrição aqui..."
                maxLength={maxLength}
                value={values.justificativa || ""}
                onChange={handleJustificativaChange}
                onBlur={() => formikRef.current.setFieldTouched("justificativa", true)}
                className={errors.justificativa && touched.justificativa ? "error" : ""}
                style={{
                  borderColor: errors.justificativa && touched.justificativa ? "#e53e3e" : "",
                  borderWidth: errors.justificativa && touched.justificativa ? "2px" : "1px",
                }}
              />
              {errors.justificativa && touched.justificativa && (
                <p style={{ color: "#e53e3e", fontSize: "14px", marginTop: "4px" }}>
                  {errors.justificativa}
                </p>
              )}
              <div className="flex justify-end w-full">
                <p className="opacity-40 px-1 desc">
                  {(values.justificativa || "").length}/{maxLength}
                </p>
              </div>
            </label>
            <button
              type="button"
              className="submit-button"
              onClick={() => formikRef.current.submitForm()}
            >
              Renovar Massivo
            </button>
          </Form>
        )}
      </Formik>
    </main>
  );
};

Content.propTypes = {
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
  selectedRows: PropTypes.array,
};

export default RenovarMassivoModal;
